import type { AssistantTone } from '@contracts/assistant';
import { createToneParser } from '@domain/assistant-tone';

/** Characters the typewriter likes to hold back, so a gap between tokens is covered instead of shown as a stall. */
export const RESERVE = 24;
/** How long the first characters wait for the reserve to fill before they show anyway. */
export const START_WAIT_MS = 250;
/** Once the reply has fully arrived, what is left drains in about this long. */
export const END_DRAIN_MS = 200;

export type Frames = { request(cb: (t: number) => void): number; cancel(id: number): void; now(): number };
export type Typewriter = {
  push(text: string): void;
  end(): void;
  /** The stored text is known: a text that continues what is shown keeps typing, another replaces it at once. */
  settle(final: string): void;
  stop(): void;
  shown(): string;
};
export type Reply = { segment(): void; delta(text: string): void; tone(): AssistantTone | null };

const MAX_DT = 100;                        // one long frame (a background tab) releases at most 100 ms' worth
const MARK = /\p{M}/u;
// The parser's OBJ and REPLY (S5 D39): a chip, or its label as text.
const CHIP = /^\[(?:(?:[^[\]\n]|\[[^[\]\n]*\]){1,120}\]\(obj:\w{1,32}:[^\s()]{1,64}|(?:[^[\]\n]|\[[^[\]\n]*\]){1,400}\]\(reply:(?:[^\n()]|\([^\n()]*\)){0,400})\)/;
// Could still become a chip: wait for the rest (not after end()). A reply's label and text may each hold one `(…)` pair.
const CHIP_OPEN = /^\[(?:[^\n()]|\([^\n()]*\)?){0,820}$/;

const WINDOW_FRAMES: Frames = {
  request: (cb) => requestAnimationFrame(cb), cancel: (id) => cancelAnimationFrame(id), now: () => performance.now(),
};

/**
 * Spec §6.4 (D9): text flows at the rate it arrives, smoothed. The release rate is the reply's average arrival
 * rate, scaled by how full the reserve is (a long backlog speeds up, a short one slows down, never under half),
 * so a 300 ms gap between tokens is covered by the reserve instead of showing as a stall.
 */
export function createTypewriter(o: { onChange(shown: string, typing: boolean): void; reduced: boolean;
  frames?: Frames }): Typewriter {
  const frames = o.frames ?? WINDOW_FRAMES;
  let target = '';
  let at = 0;
  let received = 0;
  let firstAt: number | null = null;
  let started = false;
  let ended = false;
  let credit = 0;
  let last: number | null = null;
  let id: number | null = null;

  const emit = () => o.onChange(target.slice(0, at), !ended || at < target.length);
  const schedule = () => { if (id === null) id = frames.request(frame); };
  /** Shows `n` more units: a code point with its combining marks, or a whole chip link. */
  const advance = (n: number) => {
    for (let i = 0; i < n && at < target.length; i += 1) {
      const rest = target.slice(at);
      if (rest.startsWith('[')) {
        const chip = CHIP.exec(rest);
        if (chip) { at += chip[0].length; continue; }
        if (!ended && CHIP_OPEN.test(rest)) return;
      }
      at += (target.codePointAt(at) ?? 0) > 0xffff ? 2 : 1;
      while (at < target.length && MARK.test(target[at]!)) at += 1;
    }
  };
  function frame(t: number) {
    id = null;
    const dt = last === null ? 0 : Math.min(MAX_DT, t - last);
    last = t;
    const backlog = target.length - at;
    if (!started) {
      if (!ended && backlog < RESERVE / 2 && t - (firstAt ?? t) < START_WAIT_MS) { schedule(); return; }
      started = true;
      credit = Math.max(credit, 1);                       // the first frame after the wait shows something
    }
    const inRate = received / Math.max(1, t - (firstAt ?? t));
    const rate = ended ? Math.max(backlog / END_DRAIN_MS, inRate)
      : Math.max(inRate * 0.5, inRate * (backlog / RESERVE));
    credit += dt * rate;
    const n = Math.floor(credit);
    if (n > 0) {
      credit -= n;
      const before = at;
      advance(n);
      if (at !== before) emit();
    }
    if (at < target.length) schedule();
    else { credit = 0; last = null; if (ended) emit(); }
  }
  return {
    push(text) {
      if (text === '') return;
      if (firstAt === null) firstAt = frames.now();
      target += text;
      received += text.length;
      if (o.reduced) { at = target.length; emit(); return; }
      schedule();
    },
    end() { ended = true; if (o.reduced || at === target.length) emit(); else schedule(); },
    settle(final) {
      ended = true;
      if (!o.reduced && final.startsWith(target.slice(0, at))) { target = final; schedule(); return; }
      target = final;
      at = final.length;
      emit();
    },
    stop() { if (id !== null) frames.cancel(id); id = null; },
    shown: () => target.slice(0, at),
  };
}

/** One turn's streamed reply: each model attempt (D60) is its own paragraph with its own tone label. */
export function createReply(tw: Typewriter): Reply {
  const parser = createToneParser();
  let any = false;
  let inSegment = false;
  let tone: AssistantTone | null = null;
  const feed = (text: string) => {
    if (text === '') return;
    if (any && !inSegment) tw.push('\n\n');
    any = true;
    inSegment = true;
    tw.push(text);
  };
  return {
    segment() { feed(parser.end()); tone = parser.tone() ?? tone; parser.reset(); inSegment = false; },
    delta(text) { feed(parser.push(text)); tone = parser.tone() ?? tone; },
    tone: () => tone,
  };
}
